import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Row,
  Col,
  Typography
} from 'antd';
import {
  FileTextOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  BankOutlined
} from '@ant-design/icons';
import SideBar from '../../components/SideBar';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import { useBreadcrumbTitle } from '../../contexts/BreadcrumbContext';
import ProfilingMembre from './details-membre/ProfilingMembre';
import { API_URL, LOGGED_USER, TOKEN } from '../../variables/constants';
import axiosInstance from '../../variables/api/axiosInstance';
import { useLocation, useParams } from 'react-router-dom';
import StatsSummary from './details-membre/StatsSummary';
import UtilisateursTab from './details-membre/UtilisateursTab';
import AgenceTab from './details-membre/AgenceTab';
import { rightsService } from '../../services/rightsService';
import { getCompagnieLogo } from '../../hooks/useFilesHooks';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Palette de couleurs
const colors = {
  primary: '#242166',
  secondary: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
  danger: '#ff4d4f',
  info: '#13c2c2',
  light: '#f0f2f5',
  gradient1: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
  gradient2: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  gradient3: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  gradient4: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
};

const DetailsMembre = () => {
  const { setPageTitle } = useBreadcrumbTitle();
  const [activeTab, setActiveTab] = useState('1');
  const [membre, setMembre] = useState({});
  const [globalStats, setGlobalStats] = useState({});
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [intermediaires, setIntermediaires] = useState([]);
  const [exercices, setExercices] = useState([]);
  const [agences, setAgences] = useState([]);
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState('');
  const [footerPreviewUrl, setFooterPreviewUrl] = useState('');
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [villes, setVilles] = useState([]);
  const [villeAgence, setVilleAgence] = useState('');
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (membre?.raisonSociale) setPageTitle(membre.raisonSociale);
    return () => setPageTitle(null);
  }, [membre]);

  const orderAgences = (list = []) => {
    const getTimestamp = (agence) => new Date(agence?.updatedAt || agence?.createdAt || 0).getTime();
    return [...list].sort((a, b) => getTimestamp(b) - getTimestamp(a));
  };

  const handleAgenceSaved = (agence) => {
    if (agence) {
      setAgences((prev) => {
        const existingAgence = prev.find((a) => a.codeId === agence.codeId);
        const agenceWithDates = {
          ...agence,
          createdAt: agence.createdAt || existingAgence?.createdAt,
          updatedAt: agence.updatedAt || existingAgence?.updatedAt || agence.createdAt || existingAgence?.createdAt,
        };

        return orderAgences([agenceWithDates, ...prev.filter((a) => a.codeId !== agence.codeId)]);
      });
      setActiveTab('3');
    }
    setVisible(false);
  };

  const param = useParams();
  const location = useLocation();
  const isMyCompanyPage = useMemo(() => !param.organismeCode, [param.organismeCode]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = (searchParams.get('tab') || '').toLowerCase();
    if (tabParam === 'utilisateurs') {
      setActiveTab('2');
    } else if (tabParam === 'agences') {
      setActiveTab('3');
    } else if (tabParam === 'apercu') {
      setActiveTab('1');
    }
  }, [location.search]);

  useEffect(() => {
    let codeOrg = "";
    codeOrg = param.organismeCode
      ? param.organismeCode
      : LOGGED_USER.profil.organisme.code;

    axiosInstance
      .get(`/api/organisme/code/${codeOrg}`)
      .then((res) => {
        console.log(res.data);
        setMembre(res.data);

        if (res.data.logo) {
          getCompagnieLogo(res.data.code)
            .then(async (response) => {
              const imageBytes = await response.arrayBuffer();
              var blob = new Blob([imageBytes], { type: "image/png" });
              var imageUrl = URL.createObjectURL(blob);
              setImagePreviewUrl(imageUrl);
              // Si besoin, on peut reconstruire un File object, mais pas nécessaire pour l'affichage
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          setImagePreviewUrl(null);
        }

        if (res.data.entete) {
          fetch(`${API_URL}/api/organisme/logo/${res.data.entete.split("/")[1]}`, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          })
            .then(async (response) => {
              const imageBytes = await response.arrayBuffer();
              var blob = new Blob([imageBytes], { type: "image/png" });
              var imageUrl = URL.createObjectURL(blob);
              setHeaderPreviewUrl(imageUrl);
              // Si besoin, on peut reconstruire un File object, mais pas necesario pour l'affichage
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          setHeaderPreviewUrl(null);
        }
        if (res.data.footer) {
          fetch(`${API_URL}/api/organisme/logo/${res.data.footer.split("/")[1]}`, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          })
            .then(async (response) => {
              const imageBytes = await response.arrayBuffer();
              var blob = new Blob([imageBytes], { type: "image/png" });
              var imageUrl = URL.createObjectURL(blob);
              setFooterPreviewUrl(imageUrl);
              // Si besoin, on peut reconstruire un File object, mais pas necesario para l'affichage
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          setFooterPreviewUrl(null);
        }
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/intermediaire/compagnie/${codeOrg}`)
      .then((res) => {
        setIntermediaires(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/exercice`)
      .then((res) => {
        setExercices(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/stats/global-member-stats/${codeOrg}`)
      .then((res) => {
        console.log("stats: ", res.data);
        setGlobalStats(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/agence/organisme/code/${codeOrg}`)
      .then((res) => {
        setAgences(orderAgences(res.data || []));
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/organisme/utilisateurs/code/${codeOrg}`)
      .then((res) => {
        console.log("utilisateurs: ", res.data);
        setUtilisateurs(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/parametre/type/VILLE`)
      .then((res) => {
        setVilles(res.data);
        setVilleAgence(res.data[0].code);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [param.organismeCode, visible]);

  const displayNumbers = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };





  return (
    <div className="layout-wrapper layout-content-navbar  ">
      <div className="layout-container">
        <SideBar />

        {/* <!-- Layout container --> */}
        <div className="layout-page">
          <NavBar />

          {/* <!-- Content wrapper --> */}
          <div className="content-wrapper">
            {/* <!-- Content --> */}
            <div className="container-xxl flex-grow-1 container-p-y">
              <div style={{ background: colors.light, minHeight: '100vh', padding: '24px' }}>
                {/* Header */}
                <Card
                  bordered={false}
                  style={{
                    marginBottom: '24px',
                    background: colors.gradient1,
                    borderRadius: '12px'
                  }}
                >
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Space direction="vertical" size={0}>
                        <Text style={{ color: 'white', fontSize: '16px', opacity: 0.9 }}>
                          Code membre
                        </Text>
                        <Title level={2} style={{ color: 'white', margin: 0 }}>
                          #{membre.code}
                        </Title>
                        <Text style={{ color: 'white', fontSize: '14px', opacity: 0.8 }}>
                          Date de création : {membre.dateCreation}
                        </Text>
                      </Space>
                    </Col>
                    {LOGGED_USER?.organisme?.typeOrganisme === 'POOL' && rightsService("HME047") && <Col>
                      <Button
                        danger
                        size="large"
                        icon={membre.active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                        style={{ borderRadius: '8px' }}
                      >
                        {membre.active ? 'Désactiver' : 'Activer'} le membre
                      </Button>
                    </Col>}
                  </Row>
                </Card>

                <Row gutter={[24, 24]}>
                  {/* Colonne gauche - Informations */}
                  <ProfilingMembre membre={membre} colors={colors} globalStats={globalStats} logo={imagePreviewUrl} />

                  {/* Colonne droite - Contenu principal */}
                  <Col xs={24} lg={16}>
                    <Card
                      bordered={false}
                      style={{ borderRadius: '12px' }}
                    >
                      <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        size="large"
                        style={{
                          '& .ant-tabs-tab-active': {
                            color: colors.primary
                          }
                        }}
                      >
                        {/* Onglet Aperçu */}
                        <TabPane
                          tab={
                            <span>
                              <FileTextOutlined />
                              Aperçu
                            </span>
                          }
                          key="1"
                        >
                          <StatsSummary globalStats={globalStats} colors={colors} />
                        </TabPane>

                        {/* Onglet Utilisateurs */}
                        <TabPane
                          tab={
                            <span>
                              <TeamOutlined />
                              Utilisateurs
                            </span>
                          }
                          key="2"
                        >
                          <UtilisateursTab utilisateurs={utilisateurs} colors={colors} />
                        </TabPane>

                        {/* Onglet Agences */}
                        <TabPane
                          tab={
                            <span>
                              <BankOutlined />
                              Mes Agences
                            </span>
                          }
                          key="3"
                        >
                          <AgenceTab colors={colors} agences={agences} membre={membre} visible={visible} setVisible={setVisible} onAgenceSaved={handleAgenceSaved} />
                        </TabPane>
                      </Tabs>
                    </Card>
                  </Col>
                </Row>
              </div>
            </div>
            {/* <!-- / Content --> */}

            {/* <!-- Footer --> */}
            <Footer />
            {/* <!-- / Footer --> */}

            <div className="content-backdrop fade"></div>
          </div>
          {/* <!-- Content wrapper --> */}
        </div>
        {/* <!-- / Layout page --> */}
      </div>

      {/* <!-- Overlay --> */}
      <div className="layout-overlay layout-menu-toggle"></div>

      {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
      <div className="drag-target"></div>
    </div>
  );
};

export default DetailsMembre;
